import { ChipProps } from '@swyftx/aviary/atoms/Chip/Chip.styles';
import { MarginOrderDirection, MarginPartialCloseError, MarginPosition, MarginRisk } from '@swyftx/margin/build/types';
import { AT_RISK_THRESHOLD, MONITOR_THRESHOLD, WARNING_THRESHOLD } from '@swyftx/margin/build/utils';

import { TimesEnum } from '@shared/enums';
import { Big } from '@shared/safe-big';

const HIDDEN_INDICATORS = [
  'Net Volume',
  'On Balance Volume',
  'Price Volume Trend',
  'Volume Oscillator',
  'Volume Profile Fixed Range',
  'Volume Profile Visible Range',
  'Volume',
  'VWMA',
  'VWAP',
];

const getMarginRiskTitle = (usedMargin: Big, marginLevel: Big) => {
  if (usedMargin.eq(0)) {
    return MarginRisk.None;
  } else if (marginLevel.lt(AT_RISK_THRESHOLD)) {
    return MarginRisk.StopOut;
  } else if (marginLevel.lt(WARNING_THRESHOLD)) {
    return MarginRisk.AtRisk;
  } else if (marginLevel.lt(MONITOR_THRESHOLD)) {
    return MarginRisk.Warning;
  } else {
    return MarginRisk.Monitor;
  }
};

const getMarginRiskColor = (usedMargin: Big, marginLevel: Big) => {
  const riskRating = getMarginRiskTitle(usedMargin, marginLevel);
  switch (riskRating) {
    case MarginRisk.None:
      return 'secondary';
    case MarginRisk.StopOut:
      return 'error';
    case MarginRisk.AtRisk:
      return 'error';
    case MarginRisk.Warning:
      return 'warning';
    case MarginRisk.Monitor:
      return 'info';
    default:
      riskRating satisfies never;
  }
};

const getMarginPartialCloseError = (value: string, remainingLots: string, minimumLots: string) => {
  if (Big(value).eq(remainingLots)) {
    return undefined;
  } else if (Big(value).gt(remainingLots)) {
    return MarginPartialCloseError.AboveRemainingLots;
  } else if (Big(value).lt(Big(minimumLots))) {
    return MarginPartialCloseError.BelowMinimumLots;
  } else if (Big(remainingLots).minus(value).lt(minimumLots)) {
    return MarginPartialCloseError.RemainingBelowMinimumLots;
  }

  return undefined;
};

const calculateMarginRequirements = (lots: string, value: string, contractSize = 1, leverage = 1) =>
  Big(lots).times(value).times(contractSize).div(leverage);

const getPositionOrderDirection = (position: MarginPosition) => {
  if (position.sell) return MarginOrderDirection.Short;

  return MarginOrderDirection.Long;
};

const getMarketPrecisionScale = (minLotIncrement?: string) => {
  if (!minLotIncrement) return undefined;

  return -Math.log10(Number(minLotIncrement));
};

const getTransferStatusColor = (status: string): ChipProps['color'] => {
  if (status === 'PENDING') return 'info';
  if (status === 'COMPLETED') return 'success';

  return 'destructive';
};

const mapPositions = (positions: MarginPosition[]) =>
  positions.map((p) => ({
    ...p,
    limitPrice: Big(p.limitPrice || '').eq(0) ? null : p.limitPrice,
    stopPrice: Big(p.stopPrice || '').eq(0) ? null : p.stopPrice,
    takeProfit: Big(p.takeProfit || '').eq(0) ? null : p.takeProfit,
    stopLoss: Big(p.stopLoss || '').eq(0) ? null : p.stopLoss,
  }));

const STALE_PRICE_TIME = TimesEnum.SECOND * 30;

export {
  getMarketPrecisionScale,
  getMarginRiskTitle,
  getMarginRiskColor,
  getMarginPartialCloseError,
  calculateMarginRequirements,
  getPositionOrderDirection,
  getTransferStatusColor,
  mapPositions,
  HIDDEN_INDICATORS,
  STALE_PRICE_TIME,
};
